import { Badge, Button, Input, Text, Title } from "rizzui";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  GOOGLE_CLIENT_ID,
  GOOGLE_OAUTH_URL,
  GOOGLE_REDIRECT_URI,
  GOOGLE_RESPONSE_TYPE,
  GOOGLE_SCOPE,
  GOOGLE_ACCESS_TYPE,
  GOOGLE_PROMPT,
} from "@/config/constants";
import AgencyIntegrateRepository from "@/utilities/repositories/AgencyIntegrate";
import {
  EIntegrationStatus,
  EIntegrationType,
  IIntegration,
} from "@/utilities/types/AgencyIntegration";
import toast from "react-hot-toast";
import ChatBotRepositories from "@/utilities/repositories/ChatBot";
import convertToSingaporeTime from "@/utilities/functions/convertToSingaporeTime";
import useProfile from "@/hooks/use-profile";
import { EFeatureType } from "@/utilities/types/AdvanceFeature";
import { Form } from "@/components/ui/form";
import { Controller, SubmitHandler, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { useSheetConfig, useUpdateSheetConfig } from "@/hooks/use-sheet-config";

// Telegram Bot ID schema
const telegramBotSchema = z.object({
  chatId: z.string().optional(),
  id: z.string().optional(),
});

type TelegramBotSchema = z.infer<typeof telegramBotSchema>;

// Google Sheets Configuration schema
const sheetsConfigSchema = z.object({
  sheetName: z.string().optional(),
  folderId: z.string().optional(),
});

type SheetsConfigSchema = z.infer<typeof sheetsConfigSchema>;

export default function AgencyIntegration() {
  const { profile } = useProfile();
  const [googleIntegration, setGoogleIntegration] = useState<IIntegration>();
  const [chatBotSetting, setChatBotSetting] = useState<{
    chatId: string;
    id: string;
  }>({
    chatId: "",
    id: "",
  });

  const { config: sheetConfig, refetch: refetchSheetConfig } = useSheetConfig();
  const { updateConfig } = useUpdateSheetConfig();
  const formMethodsRef = useRef<UseFormReturn<
    SheetsConfigSchema,
    unknown,
    undefined
  > | null>(null);

  const handleConnectGoogle = useCallback(async () => {
    window.open(
      `${GOOGLE_OAUTH_URL}?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${GOOGLE_REDIRECT_URI}&response_type=${GOOGLE_RESPONSE_TYPE}&scope=${GOOGLE_SCOPE}&access_type=${GOOGLE_ACCESS_TYPE}&prompt=${GOOGLE_PROMPT}`,
      "_blank"
    );
  }, []);

  const fetchGoogleIntegrated = useCallback(async () => {
    try {
      const response =
        await AgencyIntegrateRepository.getAgencyIntegratedByType(
          EIntegrationType.GOOGLE
        );
      if (response.data) {
        setGoogleIntegration(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const handleDisconnectGoogle = useCallback(async () => {
    if (!googleIntegration?.id) return;
    const toastID = toast.loading("Disconnecting from Google");
    try {
      await AgencyIntegrateRepository.disconnectIntegration(
        googleIntegration.id,
        EIntegrationType.GOOGLE
      );
      setGoogleIntegration(undefined);
      toast.success("Disconnected from Google successfully", {
        id: toastID,
      });
    } catch (error) {
      console.log(error);
      toast.error("Failed to disconnect from Google", {
        id: toastID,
      });
    }
  }, [googleIntegration?.id]);

  const handleSyncAllLeads = useCallback(async () => {
    if (!googleIntegration?.id) return;
    const toastID = toast.loading("Syncing all leads");
    try {
      await AgencyIntegrateRepository.syncAllLeads();
      toast.success("Synced all leads successfully", {
        id: toastID,
      });
    } catch (error) {
      console.log(error);
      toast.error("Failed to sync all leads", {
        id: toastID,
      });
    }
  }, [googleIntegration?.id]);

  const fetchChatBotSetting = useCallback(async () => {
    try {
      const response = await ChatBotRepositories.getAllChatBot();
      if (response.data && Array.isArray(response.data)) {
        setChatBotSetting(response.data[0]);
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    fetchGoogleIntegrated();
    fetchChatBotSetting();
  }, []);

  useEffect(() => {
    if (formMethodsRef.current && sheetConfig?.config !== undefined) {
      formMethodsRef.current.reset({
        sheetName: sheetConfig.config?.name || "Leads Sheet",
        folderId: sheetConfig.config?.folderId || "",
      });
    }
  }, [sheetConfig]);

  const onSubmitTelegramBot: SubmitHandler<TelegramBotSchema> = async (
    data
  ) => {
    if (!profile?.id) {
      toast.error("User profile not found");
      return;
    }

    const toastID = toast.loading("Saving Telegram Bot ID");
    try {
      const updateData = {
        chatId: data.chatId || "",
      };
      await ChatBotRepositories.settingChatBot(
        updateData,
        chatBotSetting?.id || ""
      );

      await fetchChatBotSetting();

      toast.success("Telegram Bot ID saved successfully", {
        id: toastID,
      });
    } catch (error) {
      console.log(error);
      toast.error("Failed to save Telegram Bot ID", {
        id: toastID,
      });
    }
  };

  const onSubmitSheetsConfig: SubmitHandler<SheetsConfigSchema> = async (
    data
  ) => {
    const toastID = toast.loading("Saving Google Sheets configuration");
    try {
      const trimmedName = data.sheetName?.trim() || "";
      const trimmedFolderId = data.folderId?.trim() || "";

      const updatePayload: {
        name: string | null;
        folderId: string | null;
      } = {
        name: trimmedName,
        folderId: trimmedFolderId,
      };

      const result = await updateConfig(updatePayload);

      await refetchSheetConfig();

      if (formMethodsRef.current) {
        formMethodsRef.current.reset({
          sheetName: result.config.name || "Leads Sheet",
          folderId: result.config.folderId || "",
        });
      }

      toast.success(
        result.message || "Google Sheets configuration saved successfully",
        {
          id: toastID,
        }
      );
    } catch (error) {
      console.log(error);
      toast.error("Failed to save Google Sheets configuration", {
        id: toastID,
      });
    }
  };

  return (
    <div className="space-y-6">
      <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
        Integrations
      </Title>
      {/* Google */}
      <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 rounded-md bg-blue-100 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-blue-600"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Title
                    as="h6"
                    className="text-base sm:text-lg font-semibold text-gray-900"
                  >
                    Google Integration
                  </Title>
                  <Badge
                    color={
                      googleIntegration?.status === EIntegrationStatus.CONNECTED
                        ? "success"
                        : "warning"
                    }
                    className="text-xs font-medium"
                  >
                    {googleIntegration?.status === EIntegrationStatus.CONNECTED
                      ? "Connected"
                      : "Not Connected"}
                  </Badge>
                </div>
                <Text className="text-xs sm:text-sm text-gray-500 mt-2">
                  Connect your Google account to sync leads
                </Text>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            {googleIntegration?.status === EIntegrationStatus.CONNECTED ? (
              <Button
                variant="outline"
                onClick={handleDisconnectGoogle}
                className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 w-full sm:w-auto"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                onClick={handleConnectGoogle}
                className="w-full sm:w-auto"
              >
                Connect Google Account
              </Button>
            )}

            {googleIntegration?.status === EIntegrationStatus.CONNECTED && (
              <button
                onClick={handleSyncAllLeads}
                disabled={
                  googleIntegration?.status !== EIntegrationStatus.CONNECTED
                }
                className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors underline text-center sm:text-left w-full sm:w-auto"
              >
                Sync All Leads
              </button>
            )}
          </div>
        </div>

        {googleIntegration?.status === EIntegrationStatus.CONNECTED && (
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mt-6">
            <div className="flex items-center gap-2 mb-2">
              <svg
                className="w-4 h-4 text-green-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <Text className="text-sm font-medium text-gray-700">
                Integration Active
              </Text>
            </div>
            <Text className="text-xs text-gray-500 break-words">
              Your Google account is connected and ready to sync leads. Last
              sync:{" "}
              {convertToSingaporeTime(new Date(googleIntegration.updatedAt))}
            </Text>
          </div>
        )}

        {googleIntegration?.status === EIntegrationStatus.CONNECTED && (
          <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm mt-6">
            <Title
              as="h6"
              className="text-base sm:text-lg font-semibold text-gray-900 mb-4"
            >
              Google Sheets Configuration
            </Title>
            <Form
              onSubmit={onSubmitSheetsConfig}
              validationSchema={sheetsConfigSchema}
              resetValues={{
                sheetName: sheetConfig?.config?.name || "Leads Sheet",
                folderId: sheetConfig?.config?.folderId || "",
              }}
              onInitForm={(methods) => {
                formMethodsRef.current = methods as UseFormReturn<
                  SheetsConfigSchema,
                  unknown,
                  undefined
                >;
              }}
            >
              {({ control, formState: { isDirty } }) => (
                <div className="flex flex-col gap-4">
                  <Controller
                    control={control}
                    name="sheetName"
                    render={({ field, fieldState }) => (
                      <div className="w-full">
                        <Input
                          label="Spreadsheet Name"
                          size="lg"
                          placeholder="Enter spreadsheet name"
                          className="w-full"
                          {...field}
                          value={field.value ?? ""}
                          error={fieldState.error?.message}
                        />
                      </div>
                    )}
                  />
                  <Controller
                    control={control}
                    name="folderId"
                    render={({ field, fieldState }) => (
                      <div className="w-full">
                        <Input
                          label="Google Drive Folder ID"
                          size="lg"
                          placeholder="Optional: Leave blank for root folder"
                          className="w-full"
                          {...field}
                          value={field.value ?? ""}
                          error={fieldState.error?.message}
                        />
                      </div>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full sm:w-auto"
                    disabled={!isDirty}
                  >
                    Save Configuration
                  </Button>
                </div>
              )}
            </Form>
          </div>
        )}
      </div>

      {profile?.userFeatures?.find(
        (item) => item?.feature?.type === EFeatureType.IpAccessTrackerTelegram
      )?.enabled && (
        <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm">
          <Title
            as="h6"
            className="text-base sm:text-lg font-semibold text-gray-900 mb-4"
          >
            Telegram Bot Configuration
          </Title>
          <Form<TelegramBotSchema>
            onSubmit={onSubmitTelegramBot}
            validationSchema={telegramBotSchema}
            resetValues={chatBotSetting}
          >
            {({ control, formState: { isDirty } }) => (
              <div className="flex flex-col gap-4">
                <Controller
                  control={control}
                  name="chatId"
                  render={({ field, fieldState }) => (
                    <Input
                      label="Telegram Bot ID"
                      size="lg"
                      placeholder="Enter Telegram Bot ID"
                      className="w-full"
                      {...field}
                      value={field.value || ""}
                      error={fieldState.error?.message}
                    />
                  )}
                />
                <Button
                  type="submit"
                  className="w-full sm:w-auto"
                  disabled={!isDirty}
                >
                  Save Telegram Bot ID
                </Button>
              </div>
            )}
          </Form>
        </div>
      )}
    </div>
  );
}
